/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { useCallback, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { SalesDashboardChartTooltip } from "./SalesDashboardChartTooltip";
import {
  chartMargin,
  commonStyles,
  createEmptyChartRow,
  createTicketNames,
  getChartColor,
} from "./chartHelper";

import type { ChartRow } from "./chartHelper";
import type {
  GetTicketSalesByOrderDateResponse,
  TicketSalesByOrderDate,
} from "./useGetTicketSalesByOrderDate";

export const TicketSalesByOrderDateDashboard: React.FC<{
  data: GetTicketSalesByOrderDateResponse | undefined;
  isLoading: boolean;
}> = ({ data, isLoading }) => {
  const ticketNames = useMemo(() => {
    if (!data) return [];
    return createTicketNames(data.ticketSalesByOrderDate);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return undefined;
    return createChartData(data.ticketSalesByOrderDate, ticketNames);
  }, [data, ticketNames]);

  const getTooltipSeriesColor = useCallback(
    (seriesName: string) => {
      const index = ticketNames.indexOf(seriesName);

      return index === -1 ? undefined : getChartColor(index);
    },
    [ticketNames]
  );

  if (isLoading || !chartData || chartData.length === 0) {
    return (
      <section css={commonStyles.section}>
        <div css={commonStyles.header}>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <h4 css={commonStyles.title}>日別販売数（券種別）</h4>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <p css={commonStyles.subtitle}>支払日ごとの販売数</p>
        </div>

        {isLoading ? (
          <div
            css={commonStyles.chartArea}
            aria-busy="true"
            // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
            aria-label="グラフを読み込み中"
          >
            <div css={commonStyles.skeleton} />
          </div>
        ) : (
          <div css={commonStyles.chartArea}>
            {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
            <span css={commonStyles.notDataText}>データがありません</span>
          </div>
        )}
      </section>
    );
  }

  return (
    <section css={commonStyles.section}>
      <div css={commonStyles.header}>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <h4 css={commonStyles.title}>日別販売数（券種別）</h4>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <p css={commonStyles.subtitle}>支払日ごとの販売数</p>
      </div>

      <ul css={commonStyles.legend}>
        {ticketNames.map((name, index) => (
          <li css={commonStyles.legendItem} key={name}>
            <span css={styles.legendMarker(getChartColor(index))} />
            <span>{name}</span>
          </li>
        ))}
      </ul>

      <div css={commonStyles.chartArea}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart barGap={1} data={chartData} margin={chartMargin}>
            <CartesianGrid stroke="#2e3d4c" vertical={false} />
            <XAxis
              axisLine={{ stroke: "#8b96a2" }}
              dataKey="label"
              label={{
                fill: "#8b96a2",
                fontSize: 12,
                position: "bottom",
                offset: 6,
                // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
                value: "支払日",
              }}
              minTickGap={18}
              tick={{ fill: "#8b96a2", fontSize: 12 }}
              tickLine={false}
              tickMargin={8}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              label={{
                angle: -90,
                fill: "#8b96a2",
                fontSize: 12,
                position: "left",
                offset: 6,
                // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
                value: "販売数",
              }}
              tick={{ fill: "#8b96a2", fontSize: 12 }}
              tickCount={5}
              tickLine={false}
              width={42}
            />
            <Tooltip
              cursor={{ fill: "rgba(31, 41, 55, 0.04)" }}
              content={
                <SalesDashboardChartTooltip
                  getSeriesColor={getTooltipSeriesColor}
                />
              }
            />
            {ticketNames.map((name, index) => (
              <Bar
                dataKey={name}
                fill={getChartColor(index)}
                fillOpacity={0.95}
                key={name}
                maxBarSize={28}
                radius={0}
                stackId="sales"
                stroke="#1b2734"
                strokeWidth={1}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

/**
 * APIの「注文日 x チケット名」の行データを、Rechartsが系列ごとに描画できる日付単位の横持ちデータへ変換する。
 *
 * 変更前: [{ orderDate, ticketName: "A", quantity: 10 }, { orderDate, ticketName: "B", quantity: 3 }]
 * 変更後: [{ label: "06/08", A: 10, B: 3 }]
 */
const createChartData = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[],
  ticketNames: string[]
): ChartRow[] => {
  const rowsByOrderDate = new Map<number, ChartRow>();

  ticketSalesByOrderDate.forEach(({ orderDate, quantity, ticketName }) => {
    const orderDateMilliseconds = Number(orderDate);
    const row =
      rowsByOrderDate.get(orderDateMilliseconds) ??
      createEmptyChartRow(orderDateMilliseconds, ticketNames);

    row[ticketName] = quantity;
    rowsByOrderDate.set(orderDateMilliseconds, row);
  });

  return Array.from(rowsByOrderDate.entries())
    .sort(([a], [b]) => a - b)
    .map(([, row]) => row);
};

const styles = {
  legendMarker: (color: string) => css`
    width: 9px;
    height: 9px;
    background: ${color};
    border-radius: 2px;
  `,
};
